import { matchPath } from 'react-router-dom';
import { routes, RouteType } from 'core/routes/Router';

export const UNKNOWN_PAGE_ID = 'unknown';
export const DASHBOARD_PAGE_ID = 'dashboard';

interface FlatRoute {
  pattern: string;
  pageId: string;
  exact?: boolean;
  literalSegments: number;
}

/** Map catch-all route to explicit page id */
const PAGE_ID_OVERRIDES: Record<string, string> = {
  '/:err?': DASHBOARD_PAGE_ID,
};

function derivePageId(pattern: string): string {
  return pattern
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      if (!segment.startsWith(':')) return segment;
      return `(${segment.slice(1).replace(/\?/g, '')})`;
    })
    .join('-')
    .toLowerCase();
}

function countLiteralSegments(pattern: string): number {
  return pattern.split('/').filter((segment) => segment && !segment.startsWith(':')).length;
}

function flatten(list: RouteType[], acc: FlatRoute[] = []): FlatRoute[] {
  for (const route of list) {
    acc.push({
      pattern: route.path,
      pageId: PAGE_ID_OVERRIDES[route.path] ?? derivePageId(route.path),
      exact: route.exact,
      literalSegments: countLiteralSegments(route.path),
    });
    if (route.routes) flatten(route.routes, acc);
  }
  return acc;
}

/**
 * Flattened lazily on first use, NOT at module load. `routes` lives in core/routes/Router,
 * which imports most of the app; if anything in that graph imports telemetry, dereferencing
 * `routes` during module evaluation throws `Cannot access 'routes' before initialization`.
 * By first call we are inside a render, so Router has finished initializing.
 */
let flatRoutesCache: FlatRoute[] | undefined;

function getFlatRoutes(): FlatRoute[] {
  if (!flatRoutesCache) flatRoutesCache = flatten(routes);
  return flatRoutesCache;
}

interface Candidate {
  route: FlatRoute;
  isExact: boolean;
  consumedLength: number;
  params: Record<string, string | undefined>;
}

function isMoreSpecific(candidate: Candidate, incumbent: Candidate): boolean {
  if (candidate.isExact !== incumbent.isExact) return candidate.isExact;
  if (candidate.consumedLength !== incumbent.consumedLength) {
    return candidate.consumedLength > incumbent.consumedLength;
  }
  return candidate.route.literalSegments > incumbent.route.literalSegments;
}

let memoPathname: string | undefined;
let memoMatch: Candidate | undefined;

function findBestMatch(pathname: string): Candidate | undefined {
  if (pathname === memoPathname) return memoMatch;

  let best: Candidate | undefined;
  for (const route of getFlatRoutes()) {
    if (!route.pageId) continue;
    const match = matchPath<Record<string, string | undefined>>(pathname, {
      path: route.pattern,
      exact: route.exact,
    });
    if (!match) continue;
    const candidate: Candidate = {
      route,
      isExact: match.isExact,
      consumedLength: match.url.length,
      params: match.params,
    };
    if (!best || isMoreSpecific(candidate, best)) best = candidate;
  }

  memoPathname = pathname;
  memoMatch = best;
  return best;
}

export function resolvePageId(pathname: string): string {
  return findBestMatch(pathname)?.route.pageId || UNKNOWN_PAGE_ID;
}

export function resolveRouteParams(pathname: string): Record<string, string | undefined> {
  return findBestMatch(pathname)?.params ?? {};
}
